import Vue from "vue";
import { UserManager, WebStorageStateStore } from "oidc-client";

export const SignInType = {
  Window: 0,
  Popup: 1,
};

export const LogLevel = {
  None: 0,
  Error: 1,
  Warn: 2,
  Info: 3,
  Debug: 4,
};

export function createOidcAuth(authName, signInType, appUrl, oidcConfig) {
  const baseUrl = appUrl.endsWith("/") ? appUrl : `${appUrl}/`;

  const config = {
    response_type: "code",
    scope: "openid profile",
    automaticSilentRenew: false,
    monitorSession: false,
    post_logout_redirect_uri: baseUrl,
    redirect_uri: `${baseUrl}auth/signinwin/${authName}`,
    popup_redirect_uri: `${baseUrl}auth/signinpop/${authName}`,
    popup_post_logout_redirect_uri: `${baseUrl}auth/signoutpop/${authName}`,
    userStore: new WebStorageStateStore({
      store: window.localStorage,
    }),
    ...oidcConfig,
    automaticSilentRenew: false,
    monitorSession: false,
  };

  const mgr = new UserManager(config);

  const state = Vue.observable({
    user: null,
  });

  const eventBus = new Vue();

  const eventMap = {
    accessTokenExpiring: "addAccessTokenExpiring",
    accessTokenExpired: "addAccessTokenExpired",
    silentRenewError: "addSilentRenewError",
    userLoaded: "addUserLoaded",
    userUnloaded: "addUserUnloaded",
    userSignedOut: "addUserSignedOut",
    userSessionChanged: "addUserSessionChanged",
  };

  Object.entries(eventMap).forEach(([eventName, methodName]) => {
    if (typeof mgr.events[methodName] === "function") {
      mgr.events[methodName]((...args) => {
        eventBus.$emit(eventName, ...args);
      });
    }
  });

  let routerInstance = null;
  let startupPromise = null;

  mgr.events.addUserLoaded((user) => {
    state.user = user;
  });

  mgr.events.addUserUnloaded(() => {
    state.user = null;
  });

  const auth = {
    appUrl: baseUrl,
    authName,
    mgr,
    events: mgr.events,

    get user() {
      return state.user;
    },

    get isAuthenticated() {
      return !!state.user && !state.user.expired;
    },

    get accessToken() {
      return state.user?.access_token || "";
    },

    get userProfile() {
      return state.user?.profile || {};
    },

    async startup() {
      if (startupPromise) return startupPromise;

      startupPromise = (async () => {
        const normalizePath = (path) => path.replace(/\/+$/, "").toLowerCase();

        const currentPath = normalizePath(window.location.pathname);

        const signInPath = normalizePath(new URL(config.redirect_uri).pathname);

        if (currentPath === signInPath) {
          const user = await mgr.signinRedirectCallback();

          state.user = user;

          const target = user?.state?.to || "/";

          const safeTarget =
            typeof target === "string" &&
            target.startsWith("/") &&
            !target.startsWith("//") &&
            !target.startsWith("/\\")
              ? target
              : "/";

          const destination = new URL(safeTarget.replace(/^\/+/, ""), baseUrl);

          window.location.replace(destination.href);

          return false;
        }

        state.user = await mgr.getUser();

        return true;
      })();

      return startupPromise;
    },

    useRouter(router) {
      routerInstance = router;

      router.beforeEach(async (to, from, next) => {
        const requiresAuth = to.matched.some(
          (record) => record.meta.authName === authName,
        );

        if (!requiresAuth) {
          return next();
        }

        if (state.user) {
          return next();
        }

        try {
          const storedUser = await mgr.getUser();

          if (storedUser) {
            state.user = storedUser;
            return next();
          }

          await auth.signIn({
            state: {
              to: to.fullPath,
            },
          });

          next(false);
        } catch (error) {
          console.error("OIDC route authentication failed", error);
          next(false);
        }
      });
    },

    signIn(args) {
      if (signInType === SignInType.Popup) {
        return mgr.signinPopup(args);
      }

      return mgr.signinRedirect(args);
    },

    signOut(args) {
      if (signInType === SignInType.Popup) {
        return mgr.signoutPopup(args);
      }

      return mgr.signoutRedirect(args);
    },

    $on(eventName, callback) {
      eventBus.$on(eventName, callback);
      return this;
    },

    $off(eventName, callback) {
      eventBus.$off(eventName, callback);
      return this;
    },

    $once(eventName, callback) {
      eventBus.$once(eventName, callback);
      return this;
    },

    startSilentRenew() {
      throw new Error(
        "Automatic silent renew is disabled by application policy",
      );
    },

    stopSilentRenew() {
      mgr.stopSilentRenew();
    },

    async refreshToken() {
      const currentUser = await mgr.getUser();

      if (!currentUser?.refresh_token) {
        const error = new Error("Refresh token unavailable");
        error.code = "REFRESH_TOKEN_UNAVAILABLE";
        throw error;
      }

      return mgr.signinSilent();
    },
  };

  return auth;
}
